import isEqual from 'lodash/isEqual';
import type {SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';
import {isSnapshotRecord} from '@PosterWhiteboard/collaboration/record-changes.library';
import {getGroupsById, getItemsById, getItemsOrder, getPageOrder, getPagesById, replaceGroups, replaceItems, replacePages} from '@PosterWhiteboard/collaboration/poster-snapshot.library';

const GROUP_ID_KEY = 'groupId';
const MIN_GROUP_MEMBER_COUNT = 2;

/**
 * Repairs what merging two people's edits can break even though each edit was valid on its own:
 * a group left with fewer than two members, an item pointing at a group that was dissolved, and
 * group members separated in the stacking order. Every replica runs this after applying the same
 * ops, reaches the same result, and so never needs to send the repair to anyone.
 *
 * Only structural rules live here. Timing rules (background coverage, timeline rows) depend on
 * media the snapshot does not hold, so the editor repairs those on the live poster.
 */
export const normalizePosterSnapshot = (snapshot: SnapshotRecord): SnapshotRecord => {
  const pagesById = getPagesById(snapshot);
  const normalizedPagesById: SnapshotRecord = {};
  let hasChanges = false;

  for (const [pageId, page] of Object.entries(pagesById)) {
    const normalizedPage = isSnapshotRecord(page) ? normalizePageGroups(page) : page;
    normalizedPagesById[pageId] = normalizedPage;
    hasChanges ||= normalizedPage !== page;
  }

  return hasChanges ? replacePages(snapshot, normalizedPagesById, getPageOrder(snapshot)) : snapshot;
};

const normalizePageGroups = (page: SnapshotRecord): SnapshotRecord => {
  const itemsById = getItemsById(page);
  const itemsOrder = getItemsOrder(page);
  const groupsById = getGroupsById(page);
  const survivingGroupIds = getGroupIdsWithEnoughMembers(itemsById, itemsOrder, groupsById);

  const normalizedItemsById = clearGroupIdsNotIn(itemsById, survivingGroupIds);
  const normalizedGroupsById = Object.fromEntries(Object.entries(groupsById).filter(([groupId]) => survivingGroupIds.has(groupId)));
  const normalizedItemsOrder = [...survivingGroupIds].reduce((order, groupId) => gatherGroupMembers(order, normalizedItemsById, groupId), itemsOrder);

  const areItemsUnchanged = normalizedItemsById === itemsById && isEqual(normalizedItemsOrder, itemsOrder);
  const areGroupsUnchanged = Object.keys(normalizedGroupsById).length === Object.keys(groupsById).length;
  if (areItemsUnchanged && areGroupsUnchanged) {
    return page;
  }
  const pageWithItems = areItemsUnchanged ? page : replaceItems(page, normalizedItemsById, normalizedItemsOrder);
  return areGroupsUnchanged ? pageWithItems : replaceGroups(pageWithItems, normalizedGroupsById);
};

const getGroupIdOfItem = (item: unknown): string | null => {
  const groupId: unknown = isSnapshotRecord(item) ? item[GROUP_ID_KEY] : null;
  return typeof groupId === 'string' ? groupId : null;
};

const getGroupIdsWithEnoughMembers = (itemsById: SnapshotRecord, itemsOrder: string[], groupsById: SnapshotRecord): Set<string> => {
  const memberCountByGroupId = new Map<string, number>();
  for (const itemId of itemsOrder) {
    const groupId = getGroupIdOfItem(itemsById[itemId]);
    if (groupId !== null && Object.hasOwn(groupsById, groupId)) {
      memberCountByGroupId.set(groupId, (memberCountByGroupId.get(groupId) ?? 0) + 1);
    }
  }
  return new Set([...memberCountByGroupId].filter(([, memberCount]) => memberCount >= MIN_GROUP_MEMBER_COUNT).map(([groupId]) => groupId));
};

const clearGroupIdsNotIn = (itemsById: SnapshotRecord, survivingGroupIds: ReadonlySet<string>): SnapshotRecord => {
  let hasChanges = false;
  const normalizedItemsById: SnapshotRecord = {};
  for (const [itemId, item] of Object.entries(itemsById)) {
    const groupId = getGroupIdOfItem(item);
    if (isSnapshotRecord(item) && groupId !== null && !survivingGroupIds.has(groupId)) {
      normalizedItemsById[itemId] = {...item, [GROUP_ID_KEY]: null};
      hasChanges = true;
    } else {
      normalizedItemsById[itemId] = item;
    }
  }
  return hasChanges ? normalizedItemsById : itemsById;
};

/** Moves a group's members directly below its frontmost member, keeping their relative order. */
const gatherGroupMembers = (itemsOrder: string[], itemsById: SnapshotRecord, groupId: string): string[] => {
  const memberIds = itemsOrder.filter((itemId) => getGroupIdOfItem(itemsById[itemId]) === groupId);
  const frontmostMemberId = memberIds.at(-1);
  if (frontmostMemberId === undefined) {
    return itemsOrder;
  }
  const memberIdSet = new Set(memberIds);
  return itemsOrder.flatMap((itemId) => {
    if (itemId === frontmostMemberId) {
      return memberIds;
    }
    return memberIdSet.has(itemId) ? [] : [itemId];
  });
};
